import {
  AVATARS,
  type Avatar as AvatarT,
  COLORS,
  type Color,
  defaultFlair,
  NICKNAME_MAX,
  nicknameSchema,
} from '@xox/shared'
import { type FormEvent, useState } from 'react'
import { Skater } from '../ice/art/Skater'
import { SWATCH_CLASSES } from '../lib/flair'
import { useSession } from '../store/session'
import { Avatar } from './Avatar'
import { Button, Input } from './ui'

interface Props {
  submitLabel?: string
  autoFocus?: boolean
  onDone?: () => void
}

export function ProfileForm({ submitLabel = 'Save', autoFocus, onDone }: Props) {
  const me = useSession((s) => s.me)
  const setProfile = useSession((s) => s.setProfile)
  const fallback = defaultFlair(me?.playerId ?? 'anon')
  const [nickname, setNickname] = useState(me?.nickname ?? '')
  const [avatar, setAvatar] = useState<AvatarT>(me?.avatar ?? fallback.avatar)
  const [color, setColor] = useState<Color>(me?.color ?? fallback.color)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const parsed = nicknameSchema.safeParse(nickname)
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid nickname')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await setProfile({ nickname: parsed.data, avatar, color })
      onDone?.()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Avatar id={me?.playerId ?? 'anon'} avatar={avatar} color={color} size="xl" />
        <div className="flex-1">
          <label htmlFor="nickname" className="text-sm font-medium">
            Your nickname
          </label>
          <Input
            id="nickname"
            name="nickname"
            value={nickname}
            maxLength={NICKNAME_MAX}
            autoComplete="nickname"
            autoCapitalize="words"
            autoFocus={autoFocus}
            placeholder="e.g. Alex"
            onChange={(e) => setNickname(e.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'nickname-error' : undefined}
            className="mt-1"
          />
        </div>
      </div>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium">Avatar</legend>
        <div className="grid grid-cols-8 gap-1">
          {AVATARS.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAvatar(a)}
              aria-pressed={avatar === a}
              aria-label={`Avatar ${a}`}
              className={[
                'flex aspect-square items-center justify-center rounded-xl text-xl leading-none transition-transform hover:scale-110',
                avatar === a
                  ? 'bg-slate-900 ring-2 ring-slate-900 dark:bg-white dark:ring-white'
                  : 'bg-slate-100 dark:bg-slate-800',
              ].join(' ')}
            >
              {a}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium">Color · your skater's shirt</legend>
        <div className="flex items-center gap-3">
          <Skater color={color} label={`${color} shirt`} className="h-14" />
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-pressed={color === c}
                aria-label={`Color ${c}`}
                className={[
                  'size-8 rounded-full transition-transform hover:scale-110',
                  SWATCH_CLASSES[c],
                  color === c
                    ? 'ring-2 ring-slate-900 ring-offset-2 dark:ring-white dark:ring-offset-slate-900'
                    : '',
                ].join(' ')}
              />
            ))}
          </div>
        </div>
      </fieldset>

      {error && (
        <p id="nickname-error" className="text-sm text-rose-600">
          {error}
        </p>
      )}
      <Button type="submit" disabled={busy}>
        {submitLabel}
      </Button>
    </form>
  )
}
